import React from 'react';
import styled from 'styled-components';
import ToggleIOS from '../../../../../../generic/ui/Toggles/ToggleIOS';
import { FieldSettingEditState, FieldPermissionKey } from '../types';
import { FIELD_PERMISSION_LABELS } from '../helpers/fieldHelpers';

type Props = {
  field: FieldSettingEditState;
  onChange: (fieldId: number, key: FieldPermissionKey, value: boolean | null) => void;
};

const TOGGLE_FIELDS: {
  key: FieldPermissionKey;
  getValue: (f: FieldSettingEditState) => boolean;
  getDefaultHint?: (f: FieldSettingEditState) => string;
}[] = [
  {
    key: 'canView',
    getValue: (f) => f.canView,
  },
  {
    key: 'isRequiredOverride',
    getValue: (f) => f.isRequiredOverride ?? f.defaultIsRequired,
    getDefaultHint: (f) => `ברירת מחדל: ${f.defaultIsRequired ? 'כן' : 'לא'}`,
  },
  {
    key: 'isEditableOverride',
    getValue: (f) => f.isEditableOverride ?? f.defaultIsEditable,
    getDefaultHint: (f) => `ברירת מחדל: ${f.defaultIsEditable ? 'כן' : 'לא'}`,
  },
];

const FieldSettingCard: React.FC<Props> = ({ field, onChange }) => {
  const displayName = field.fieldDisplayNameHe || field.fieldDisplayName;

  const handleToggle = (key: FieldPermissionKey, newValue: boolean) => {
    if (key === 'isRequiredOverride' || key === 'isEditableOverride') {
      onChange(field.fieldId, key, newValue);
      return;
    }
    onChange(field.fieldId, key, newValue);
  };

  return (
    <CardWrapper $isDirty={field.isDirty}>
      <CardHeader>
        <FieldName>{displayName}</FieldName>
        <FieldKey>{field.fieldSystemKey}</FieldKey>
      </CardHeader>
      <TogglesGrid>
        {TOGGLE_FIELDS.map(({ key, getValue, getDefaultHint }) => (
          <ToggleRow key={key}>
            <ToggleLabel>
              {FIELD_PERMISSION_LABELS[key]}
              {getDefaultHint && (
                <DefaultHint>{getDefaultHint(field)}</DefaultHint>
              )}
            </ToggleLabel>
            <ToggleIOS
              index={`${field.fieldId}-${key}`}
              defaultChecked={getValue(field)}
              onChange={(checked) => handleToggle(key, checked)}
              disabled={field.systemDefault}
            />
          </ToggleRow>
        ))}
      </TogglesGrid>
      {field.isDirty && <DirtyIndicator />}
    </CardWrapper>
  );
};

export default FieldSettingCard;

const CardWrapper = styled.div<{ $isDirty: boolean }>`
  position: relative;
  width: 100%;
  padding: 1em;
  background: #ffffff;
  border-radius: 16px;
  border: 2px solid ${(props) => (props.$isDirty ? '#f59e0b' : 'transparent')};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.75em;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  direction: rtl;
`;

const FieldName = styled.span`
  font-weight: 600;
  font-size: 1em;
  color: var(--primary-green, #297f76);
`;

const FieldKey = styled.span`
  font-size: 0.75em;
  color: #9ca3af;
  direction: ltr;
`;

const TogglesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  direction: rtl;
  padding: 0.4em 0.5em;
  border-radius: 8px;
  background: #f8f9fa;
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15em;
  align-items: flex-start;
  font-size: 0.9em;
  color: #4b5563;
`;

const DefaultHint = styled.span`
  font-size: 0.75em;
  color: #9ca3af;
`;

const DirtyIndicator = styled.div`
  position: absolute;
  top: 0.5em;
  left: 0.5em;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
`;
